import React, { FC, Fragment } from 'react';
import { IconButton, List, ListItem, ListItemText, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import RemoveIcon from '@mui/icons-material/Remove';
import { User } from 'models/user';
import { useMutation } from '@apollo/client';
import { removeLinkedDentistTemplate } from 'apollo/templates/order/mutation.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LinkedUser } from 'models/order';
import InfoText from 'components/shared/info-text.component';

type LinkedDentistsListProps = {
    orderId: string;
    linkedDentists: LinkedUser[];
    onRemove: (dentist: User) => void;
};
const LinkedDentistsList: FC<LinkedDentistsListProps> = ({
    orderId,
    linkedDentists,
    onRemove,
}: LinkedDentistsListProps) => {
    const { t: translate } = useTranslation('orders');
    const { showBoundary } = useDenthubErrorBoundary();

    const [removeLinkedDentist] = useMutation(removeLinkedDentistTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const onRemoveClick = (dentist: User) => {
        onRemove(dentist);
        removeLinkedDentist({ variables: { orderId: orderId, userId: dentist._id } });
    };

    return (
        <Fragment>
            {linkedDentists.length > 0 ? (
                <Fragment>
                    <Typography variant="subtitle1">{translate('THIS_ORDER_IS_SHARED_WITH')}:</Typography>
                    <Paper variant="outlined">
                        <List dense>
                            {linkedDentists.map((dentist) => (
                                <ListItem
                                    key={dentist.user._id}
                                    secondaryAction={
                                        <IconButton
                                            edge="end"
                                            onClick={() => {
                                                onRemoveClick(dentist.user);
                                            }}
                                            data-test={`linked-dentists-list.remove-btn-${dentist.user._id}`}
                                        >
                                            <RemoveIcon />
                                        </IconButton>
                                    }
                                    data-test={`linked-dentists-list.item-${dentist.user._id}`}
                                >
                                    <ListItemText>
                                        {dentist.user.fullName}
                                        <Typography variant="body2" color="grey.700">
                                            {dentist.user.email}
                                        </Typography>
                                    </ListItemText>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Fragment>
            ) : (
                <InfoText text={translate('THIS_ORDER_IS_NOT_SHARED')} />
            )}
        </Fragment>
    );
};

export default LinkedDentistsList;
